import { useCallback, useEffect, useState } from "react";

import { formatToLongDate } from "../../../utils/helpers.ts";
import { useEmployeePhotoSource } from "../employee-view-directory/hooks/use-employee-photo-source.ts";
import { Avatar } from "../../ui/avatar/avatar.tsx";
import { Button, Select, TextField } from "../../ui/index.ts";
import { searchAppraisals } from "./appraisal-api.ts";
import type { AppraisalRow, SearchFilters } from "./types.ts";
import styles from "./appraisal-workflow.module.css";

export const STATUS_OPTIONS = ["", "REJECTED", "APPROVED", "PDF_GENERATED", "EMAIL_SCHEDULED", "EMAIL_SENT"].map((value) => ({ value, label: value ? statusLabel(value) : "All Status" }));
export function statusLabel(status?: string | null) {
  const normalizedStatus = status?.trim();
  if (!normalizedStatus) return "-";
  return normalizedStatus.toLowerCase().split("_").map((word) => word[0]?.toUpperCase() + word.slice(1)).join(" ");
}
export const normalizeUtc = (value: string): string => {
  if (!value) return value;
  if (/[zZ]|[+-]\d{2}:?\d{2}$/.test(value)) return value;
  return `${value.replace(" ", "T")}Z`;
};

export const formatToLocalDateTime = (value?: string | null): string => {
  if (!value) return "";
  const date = new Date(normalizeUtc(value));
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleString();
};

export function formatDate(value?: string | null) {
  if (!value) return "-";
  const dateLabel = formatToLongDate(value, value);
  if (!value.includes("T")) return dateLabel;
  // Backend timestamps without an offset are UTC and must be normalized before display.
  const date = new Date(normalizeUtc(value));
  if (Number.isNaN(date.getTime())) return dateLabel;
  const localDateLabel = date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
  const localTimeLabel = date.toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
  return `${localDateLabel}, ${localTimeLabel}`;
}

export function StatusBadge({ status }: { status?: string | null }) {
  const normalizedStatus = status?.trim() ?? "";
  const statusClass = normalizedStatus
    ? styles[`status${normalizedStatus.replaceAll("_", "")}`] ?? ""
    : "";
  return <span className={`${styles.status} ${statusClass}`}>{statusLabel(normalizedStatus)}</span>;
}

export function AppraisalEmployeeAvatar({ employeeName, photoPath }: { employeeName: string; photoPath?: string | null }) {
  const photoSource = useEmployeePhotoSource(photoPath ?? undefined);
  return <Avatar name={employeeName} src={photoSource} size="md" />;
}

export function useAppraisalSearch(initialStatus = "", useHrStatus?: boolean) {
  const [filters, setFilters] = useState<SearchFilters>({ employeeId: "", status: initialStatus, useHrStatus, letterType: "", fromDate: "", toDate: "", page: 0, size: 20 });
  const [rows, setRows] = useState<AppraisalRow[]>([]); const [totalPages, setTotalPages] = useState(1); const [totalElements, setTotalElements] = useState(0); const [loading, setLoading] = useState(false); const [error, setError] = useState("");
  const load = useCallback(async () => { setLoading(true); setError(""); try { const result = await searchAppraisals(filters); setRows(result.rows); setTotalPages(result.totalPages); setTotalElements(result.totalElements); } catch (e) { setError(e instanceof Error ? e.message : "Unable to load appraisals."); } finally { setLoading(false); } }, [filters]);
  useEffect(() => { void load(); }, [load]);
  const update = (patch: Partial<SearchFilters>) => setFilters((current) => ({ ...current, ...patch }));
  return { filters, rows, totalPages, totalElements, loading, error, update, load };
}

export function SearchBar({ filters, showStatus = false, onChange, onReset }: { filters: SearchFilters; showStatus?: boolean; onChange: (patch: Partial<SearchFilters>) => void; onReset: () => void }) {
  return <div className={styles.filters}>
    <TextField id="appraisal-employee-search" label="Employee ID" placeholder="Enter employee ID" type="number" min="1" value={filters.employeeId} onChange={(e) => onChange({ employeeId: e.target.value, page: 0 })} />
    {showStatus ? <Select id="appraisal-status" label="Status" value={filters.status} options={STATUS_OPTIONS} onChange={(status) => onChange({ status, page: 0 })} /> : null}
    <TextField id="appraisal-from-date" label="From Date" type="date" value={filters.fromDate} onChange={(e) => onChange({ fromDate: e.target.value, page: 0 })} />
    <TextField id="appraisal-to-date" label="To Date" type="date" value={filters.toDate} min={filters.fromDate || undefined} onChange={(e) => onChange({ toDate: e.target.value, page: 0 })} />
    <div className={styles.filterActions}><Button variant="muted-outline" onClick={onReset}>Reset</Button><Button onClick={() => onChange({ page: 0 })}>Search</Button></div>
  </div>;
}

export function Pager({ page, totalPages, totalElements, size, onChange }: { page: number; totalPages: number; totalElements: number; size: number; onChange: (patch: Partial<SearchFilters>) => void }) {
  return <div className={styles.pager}><span>Showing {totalElements ? page * size + 1 : 0}–{Math.min((page + 1) * size, totalElements)} of {totalElements}</span><div><Button size="sm" variant="muted-outline" disabled={page === 0} onClick={() => onChange({ page: page - 1 })}>Previous</Button><span>Page {page + 1} of {totalPages}</span><Button size="sm" variant="muted-outline" disabled={page + 1 >= totalPages} onClick={() => onChange({ page: page + 1 })}>Next</Button></div></div>;
}
